import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, Save, Calendar, Trash2 } from "lucide-react";
import { api } from "../../lib/api";

function formatDate(iso) {
  if (!iso) return "Unknown date";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function ContactCard({ contact, onChanged }) {
  const [notes, setNotes] = useState(contact.notes || "");
  const [contacted, setContacted] = useState(!!contact.contacted);
  const [contactedAt, setContactedAt] = useState(contact.contacted_at || null);
  const [read, setRead] = useState(!!contact.read);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Auto-disarm the delete confirmation after a few seconds.
  useEffect(() => {
    if (!confirming) return;
    const t = setTimeout(() => setConfirming(false), 4000);
    return () => clearTimeout(t);
  }, [confirming]);

  const remove = async () => {
    if (!confirming) { setConfirming(true); return; }
    setDeleting(true);
    try {
      await api.delete(`/admin/contacts/${contact._id}`);
      toast.success("Inquiry deleted");
      onChanged && onChanged();
    } catch {
      toast.error("Could not delete");
      setDeleting(false);
      setConfirming(false);
    }
  };

  // Keep the "NEW" state in sync when the parent re-fetches (e.g. Mark all as read).
  useEffect(() => { setRead(!!contact.read); }, [contact.read]);

  const dirty = notes !== (contact.notes || "") || contacted !== !!contact.contacted;

  const save = async () => {
    setSaving(true);
    try {
      // Saving = handling the inquiry, so mark it read.
      const { data } = await api.patch(`/admin/contacts/${contact._id}`, { notes, contacted, read: true });
      setContactedAt(data.contacted_at || null);
      setRead(true);
      contact.notes = data.notes;
      contact.contacted = data.contacted;
      contact.contacted_at = data.contacted_at;
      contact.read = true;
      toast.success("Saved");
      onChanged && onChanged();
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const markRead = async () => {
    try {
      await api.patch(`/admin/contacts/${contact._id}`, { read: true });
      setRead(true);
      contact.read = true;
      onChanged && onChanged();
    } catch {
      toast.error("Could not update");
    }
  };

  return (
    <div
      className={`rounded-2xl border bg-white p-5 transition-all ${read ? "border-line" : "border-crimson/40 ring-2 ring-crimson/30"}`}
      data-testid={`contact-item-${contact._id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {!read && (
            <span className="rounded-full bg-crimson px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white" data-testid={`contact-new-badge-${contact._id}`}>
              New
            </span>
          )}
          <div className="font-bold text-ink">
            {contact.name} · <span className="font-medium text-muted-foreground">{contact.email}</span>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">{contact.phone}</div>
      </div>

      <div className="mt-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground" data-testid={`contact-date-${contact._id}`}>
        <Calendar className="h-3.5 w-3.5" /> Received {formatDate(contact.created_at)}
      </div>

      {contact.interest && <div className="mt-2 text-sm font-medium text-crimson">Interested in: {contact.interest}</div>}
      <p className="mt-2 text-sm text-muted-foreground">{contact.message}</p>

      <div className="mt-4 border-t border-line pt-4">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Internal notes</label>
          <button
            type="button"
            onClick={() => setContacted((v) => !v)}
            data-testid={`contact-toggle-contacted-${contact._id}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold transition-colors ${
              contacted ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-line bg-white text-muted-foreground hover:border-ink"
            }`}
          >
            {contacted ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
            {contacted ? "Contacted" : "Mark contacted"}
          </button>
        </div>

        {contacted && contactedAt && (
          <div className="mt-1 text-xs text-emerald-700" data-testid={`contact-contacted-at-${contact._id}`}>
            Contacted {formatDate(contactedAt)}
          </div>
        )}

        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="e.g. Called 8/12, left voicemail. Following up Monday…"
          data-testid={`contact-notes-${contact._id}`}
          className="mt-2 w-full rounded-xl border border-line bg-alt p-3 text-sm text-ink outline-none focus:border-ink"
        />

        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={remove}
            disabled={deleting}
            data-testid={`contact-delete-${contact._id}`}
            className={`mr-auto inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-bold transition-colors disabled:opacity-40 ${
              confirming ? "border-crimson bg-crimson text-white" : "border-line bg-white text-crimson hover:border-crimson"
            }`}
          >
            <Trash2 className="h-4 w-4" /> {deleting ? "Deleting…" : confirming ? "Confirm delete" : "Delete"}
          </button>
          {!read && (
            <button
              type="button"
              onClick={markRead}
              data-testid={`contact-markread-${contact._id}`}
              className="rounded-full border border-line px-4 py-2 text-sm font-bold text-ink transition-colors hover:border-ink"
            >
              Mark as read
            </button>
          )}
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            data-testid={`contact-save-${contact._id}`}
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-crimson disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
